import { Transition } from "@headlessui/react"
import { Form, Head, usePage } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import DeleteUser from "@/components/delete-user"
import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { settingsProfilePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

export default function Profile() {
  const { t } = useTranslation()
  const { auth } = usePage().props as {
    auth: { user: { name: string; language?: string } }
  }

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("settings.profile.title"),
      href: settingsProfilePath(),
    },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title={t("settings.profile.heading")}
            description={t("settings.profile.description")}
          />

          <Form
            method="patch"
            action={settingsProfilePath()}
            options={{
              preserveScroll: true,
            }}
            className="space-y-6"
          >
            {({ errors, processing, recentlySuccessful }) => (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="name">
                    {t("settings.profile.name_label")}
                  </Label>

                  <Input
                    id="name"
                    name="name"
                    className="mt-1 block w-full"
                    defaultValue={auth.user.name}
                    required
                    autoComplete="name"
                    placeholder={t("settings.profile.name_placeholder")}
                  />

                  <InputError className="mt-2" message={errors.name} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="language">
                    {t("settings.profile.language_label")}
                  </Label>

                  <Select
                    name="language"
                    defaultValue={auth.user.language || "en"}
                  >
                    <SelectTrigger id="language">
                      <SelectValue
                        placeholder={t("settings.profile.language_placeholder")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="am">አማርኛ (Amharic)</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="es">Español</SelectItem>
                      <SelectItem value="fr">Français</SelectItem>
                      <SelectItem value="it">Italiano</SelectItem>
                      <SelectItem value="ja">日本語 (Japanese)</SelectItem>
                      <SelectItem value="ko">한국어 (Korean)</SelectItem>
                      <SelectItem value="pt-BR">Português (Brasil)</SelectItem>
                      <SelectItem value="zh-CN">中文 (简体)</SelectItem>
                    </SelectContent>
                  </Select>

                  <InputError className="mt-2" message={errors.language} />
                </div>

                <div className="flex items-center gap-4">
                  <Button disabled={processing}>{t("common.save")}</Button>

                  <Transition
                    show={recentlySuccessful}
                    enter="transition ease-in-out"
                    enterFrom="opacity-0"
                    leave="transition ease-in-out"
                    leaveTo="opacity-0"
                  >
                    <p className="text-sm text-neutral-600">
                      {t("settings.profile.saved")}
                    </p>
                  </Transition>
                </div>
              </>
            )}
          </Form>
        </div>

        <DeleteUser />
      </SettingsLayout>
    </AppLayout>
  )
}
